<template>
  <AIVDialog :visible.sync="dialogVisible" title="Import Caregiver Compliance from ADP" width="900px">
    <!-- Step 1: Upload CSV -->
    <div v-if="step === 'upload'" class="import-step">
      <p class="import-description">
        Upload an ADP compliance export CSV to import expiration dates for caregiver compliance items.
        Only caregivers and document types with <strong>no existing records</strong> in OPPO will be imported.
      </p>
      <p class="import-description" style="margin-top: 4px; font-size: 12px; color: #909399;">
        Expected columns: <strong>First Name</strong>, <strong>Last Name</strong>, <strong>Email</strong> (optional),
        <strong>Document Type</strong>, <strong>Expiration Date</strong>
      </p>
      <p class="import-description" style="margin-top: 4px; font-size: 12px; color: #909399;">
        Valid document types: Drivers License, Auto Insurance, Auto Registration, CPR, SOAR, ABI/TBI Certification
      </p>
      <div class="upload-area">
        <input
          ref="fileInput"
          type="file"
          accept=".csv"
          style="display: none"
          @change="handleFileSelect"
        />
        <el-button type="primary" @click="$refs.fileInput.click()">
          <i class="fa fa-upload" style="margin-right: 6px"></i> Select CSV File
        </el-button>
        <span v-if="fileName" class="file-name">{{ fileName }}</span>
      </div>
    </div>

    <!-- Step 2: Preview -->
    <div v-if="step === 'preview'" class="import-step">
      <div v-if="previewing" class="text-center py-4">
        <i class="fa fa-spinner fa-spin" style="color: #fff;"></i>
        <span style="color: #fff;"> Analyzing import...</span>
      </div>
      <div v-else>
        <div class="preview-summary">
          <div class="summary-item summary-item--create">
            <span class="summary-count">{{ previewResults.created }}</span>
            <span class="summary-label">Will Import</span>
          </div>
          <div class="summary-item summary-item--existing">
            <span class="summary-count">{{ previewResults.skipped_existing }}</span>
            <span class="summary-label">Already Exist</span>
          </div>
          <div class="summary-item summary-item--invalid">
            <span class="summary-count">{{ previewResults.skipped_invalid }}</span>
            <span class="summary-label">Invalid / Not Found</span>
          </div>
        </div>

        <div class="preview-table-wrapper">
          <table class="preview-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Name</th>
                <th>Email</th>
                <th>Document Type</th>
                <th>Expiration Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in previewResults.rows"
                :key="row.row"
                :class="'preview-row--' + row.action"
              >
                <td>{{ row.row }}</td>
                <td>{{ row.first_name }} {{ row.last_name }}</td>
                <td>{{ row.email || '-' }}</td>
                <td>{{ row.document_type }}</td>
                <td>{{ row.expiration_date || '-' }}</td>
                <td>
                  <span v-if="row.action === 'create'" class="action-badge action-badge--create">Import</span>
                  <span v-else class="action-badge action-badge--skip" :title="row.reason">Skip <span style="font-size: 11px; opacity: 0.8;">({{ row.reason }})</span></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Step 3: Results -->
    <div v-if="step === 'results'" class="import-step">
      <div class="results-summary">
        <div class="results-icon">
          <i class="fa fa-check-circle" style="color: #67c23a; font-size: 48px;"></i>
        </div>
        <p class="results-message">Import complete</p>
        <div class="preview-summary">
          <div class="summary-item summary-item--create">
            <span class="summary-count">{{ importResults.created }}</span>
            <span class="summary-label">Imported</span>
          </div>
          <div class="summary-item summary-item--existing">
            <span class="summary-count">{{ importResults.skipped_existing }}</span>
            <span class="summary-label">Skipped (existing)</span>
          </div>
          <div class="summary-item summary-item--invalid">
            <span class="summary-count">{{ importResults.skipped_invalid }}</span>
            <span class="summary-label">Skipped (invalid)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer actions -->
    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleClose">{{ step === 'results' ? 'Close' : 'Cancel' }}</el-button>
        <el-button
          v-if="step === 'preview' && !previewing"
          @click="step = 'upload'"
        >
          Back
        </el-button>
        <el-button
          v-if="step === 'upload'"
          type="primary"
          :disabled="!parsedItems.length"
          :loading="previewing"
          @click="runPreview"
        >
          Preview Import
        </el-button>
        <el-button
          v-if="step === 'preview' && !previewing && previewResults.created > 0"
          type="success"
          :loading="importing"
          @click="runImport"
        >
          <i class="fa fa-check"></i> Confirm Import ({{ previewResults.created }} items)
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { caregiverComplianceService } from "@/serviceHandlers/caregiverComplianceService";

export default {
  name: "ImportAdpComplianceModal",
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      step: "upload", // upload -> preview -> results
      fileName: "",
      parsedItems: [],
      previewing: false,
      importing: false,
      previewResults: { created: 0, skipped_existing: 0, skipped_invalid: 0, rows: [] },
      importResults: null,
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
  },
  watch: {
    visible(val) {
      if (val) this.reset();
    },
  },
  methods: {
    reset() {
      this.step = "upload";
      this.fileName = "";
      this.parsedItems = [];
      this.previewing = false;
      this.importing = false;
      this.previewResults = { created: 0, skipped_existing: 0, skipped_invalid: 0, rows: [] };
      this.importResults = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    handleClose() {
      if (this.step === "results") {
        this.$emit("imported");
      }
      this.dialogVisible = false;
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.fileName = file.name;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.parsedItems = this.parseCsv(e.target.result);
      };
      reader.readAsText(file);
    },
    parseCsv(text) {
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return [];

      // Parse header — normalize to snake_case
      const rawHeaders = this.splitCsvLine(lines[0]);
      const headers = rawHeaders.map((h) =>
        h.trim().toLowerCase().replace(/\s+/g, "_")
      );

      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = this.splitCsvLine(lines[i]);
        if (vals.length === 0 || vals.every((v) => !v.trim())) continue;

        const row = {};
        headers.forEach((h, idx) => {
          row[h] = (vals[idx] || "").trim();
        });

        items.push({
          first_name: row.first_name || "",
          last_name: row.last_name || "",
          email: row.email || "",
          document_type: row.document_type || "",
          expiration_date: row.expiration_date || "",
        });
      }
      return items;
    },
    splitCsvLine(line) {
      // Handle quoted fields with commas
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result;
    },
    async runPreview() {
      this.step = "preview";
      this.previewing = true;
      try {
        this.previewResults = await caregiverComplianceService.importAdpCompliance(
          this.parsedItems,
          true
        );
      } catch (err) {
        this.$notifyError("Preview failed: " + (err.message || err));
        this.step = "upload";
      } finally {
        this.previewing = false;
      }
    },
    async runImport() {
      this.importing = true;
      try {
        this.importResults = await caregiverComplianceService.importAdpCompliance(
          this.parsedItems,
          false
        );
        this.step = "results";
      } catch (err) {
        this.$notifyError("Import failed: " + (err.message || err));
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.import-step {
  min-height: 120px;
}
.import-description {
  color: #c0c4cc;
  margin-bottom: 12px;
  line-height: 1.5;
}
.upload-area {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.file-name {
  color: #e0e0e0;
  font-size: 13px;
}

/* Preview summary boxes */
.preview-summary {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.summary-item {
  flex: 1;
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
}
.summary-item--create { border-left: 4px solid #67c23a; }
.summary-item--existing { border-left: 4px solid #e6a23c; }
.summary-item--invalid { border-left: 4px solid #f56c6c; }
.summary-count {
  display: block;
  font-size: 28px;
  font-weight: 600;
  color: #fff;
}
.summary-label {
  font-size: 12px;
  color: #909399;
}

/* Preview table */
.preview-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.preview-table th {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c4cc;
  padding: 8px 10px;
  text-align: left;
  position: sticky;
  top: 0;
}
.preview-table td {
  padding: 6px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: #e0e0e0;
}
.preview-row--create td { background: rgba(103, 194, 58, 0.06); }
.preview-row--skip td { background: rgba(255, 255, 255, 0.02); opacity: 0.7; }

.action-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}
.action-badge--create {
  background: rgba(103, 194, 58, 0.2);
  color: #67c23a;
}
.action-badge--skip {
  background: rgba(255, 255, 255, 0.08);
  color: #909399;
}

/* Results */
.results-summary {
  text-align: center;
  padding: 20px 0;
}
.results-icon {
  margin-bottom: 12px;
}
.results-message {
  font-size: 18px;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 20px;
}

</style>
